import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tag,
  TagLabel,
  Text,
  Wrap,
  WrapItem,
  useToast,
} from "@chakra-ui/react";
import * as React from "react";
import { useQuery, useQueryClient } from "react-query";
import { organizationService } from "api/organization-administration";
import templatePackageService from "api/issb/templatepackage.service";
import { useAuth } from "context";
import { esgAdminKeys } from "hooks/query/esg-admin/esg-admin";
import LocalSpinner from "components/LocalSpinner";

const FrameworkTagsPopup = ({ organization, isOpen, onClose }) => {
  const [selectedTags, setSelectedTags] = React.useState([]);
  const toast = useToast();
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth();
  const [isSaving, setIsSaving] = React.useState(false);

  // When accessTags is empty/null, it means "all frameworks" — pre-select all tags
  const [isAllByDefault, setIsAllByDefault] = React.useState(false);

  React.useEffect(() => {
    if (organization?.accessTags?.length > 0) {
      setSelectedTags([...organization.accessTags]);
      setIsAllByDefault(false);
    } else {
      setSelectedTags([]);
      setIsAllByDefault(true);
    }
  }, [organization]);

  // Fetch all framework tags from template packages
  const { data: frameworks, isLoading } = useQuery({
    queryKey: "templatePackageService",
    queryFn: async () => {
      await getAccessTokenSilently();
      return templatePackageService.list();
    },
    enabled: isOpen,
  });

  const allTags = React.useMemo(() => {
    const tagsSet = new Set();
    frameworks?.content?.forEach((fw) => fw.tags?.forEach((tag) => tagsSet.add(tag)));
    organization?.accessTags?.forEach((tag) => tagsSet.add(tag));
    return [...tagsSet].sort((a, b) => a.localeCompare(b));
  }, [frameworks, organization]);

  // When "all by default" and tags have loaded, pre-select all
  React.useEffect(() => {
    if (isAllByDefault && allTags.length > 0) {
      setSelectedTags([...allTags]);
    }
  }, [isAllByDefault, allTags]);

  const handleToggle = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleSave = () => {
    if (!organization) return;
    setIsSaving(true);
    // If all tags selected, save as empty (meaning "all frameworks")
    const tagsToSave = selectedTags.length === allTags.length ? [] : selectedTags;
    organizationService
      .update(organization.tenantId, { ...organization, accessTags: tagsToSave })
      .then(() => {
        toast({
          title: "Framework tags updated",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        queryClient.invalidateQueries(esgAdminKeys.orgs());
        onClose();
      })
      .catch((error) => {
        toast({
          title: "Error updating framework tags",
          description: error?.message || "An error occurred.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      })
      .finally(() => setIsSaving(false));
  };

  const isAllSelected = selectedTags.length === allTags.length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent bg="pureWhite">
        <ModalHeader>Manage Framework Tags</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Text fontSize="sm" color="gray.500" mb={4}>
            Select the framework tags for {organization?.name || "this organization"}. Leave blank for access to all frameworks.
          </Text>
          {isLoading ? (
            <LocalSpinner />
          ) : allTags.length === 0 ? (
            <Text fontSize="sm" color="gray.400">No framework tags available.</Text>
          ) : (
            <Wrap spacing={2}>
              {allTags.map((tag) => {
                const isSelected = selectedTags.includes(tag);
                return (
                  <WrapItem key={tag}>
                    <Tag
                      size="sm"
                      borderRadius="full"
                      bg={isSelected ? "lightLime" : "gray.100"}
                      border="1px solid"
                      borderColor={isSelected ? "lime" : "gray.300"}
                      color={isSelected ? "inherit" : "gray.400"}
                      px={3}
                      py={1}
                      cursor="pointer"
                      onClick={() => handleToggle(tag)}
                    >
                      <TagLabel>{tag}</TagLabel>
                      <Box as="span" ml={1} fontSize="xs">{isSelected ? "✕" : "+"}</Box>
                    </Tag>
                  </WrapItem>
                );
              })}
              <WrapItem>
                <Button
                  variant="link"
                  size="xs"
                  color="gray.500"
                  fontWeight="normal"
                  onClick={() => setSelectedTags(isAllSelected ? [] : [...allTags])}
                >
                  {isAllSelected ? "Deselect all" : "Select all"}
                </Button>
              </WrapItem>
            </Wrap>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} isLoading={isSaving}>
              Save
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FrameworkTagsPopup;
